/**
 * @module Setup
 * @author Jacob Viertel <jv@onscreen.net>
 * @see [Compile](./compile.js) triggers the setup.
 */

import {create} from "@acng/frontend-bounty/object.js";
import {isDefined} from "@acng/frontend-bounty/types.js";

const MODULE = "stargazer/registry";
const VERBOSE = false;
DEBUG: if (VERBOSE) console.warn("Import verbose", MODULE);

/**
 * @example
 * ```js
 * defineElementSetup('my-custom-element', (name) => {
 *   console.log(`stargazer found the first "<${name}>".`);
 *
 *   // lazy
 *   customElementRegistry.define(name, class extends HTMLElement {
 *     connectedCallback() {
 *       console.log(`${name} could have superpowers!`);
 *     }
 *   });
 * });
 * ```
 *
 * @template {string} T
 *
 * @param {T} name
 * The `name` specifies an {@link !Element} by its
 * [localName](https://developer.mozilla.org/docs/Web/API/Element/localName).
 *
 * @param {import('./types').Setup<T>} setup
 * The `setup` will be executed **once**, when stargazer detects a specified {@link !Element}
 * for the first time while compiling.
 *
 * TODO consider rename into `ElementSetup`
 */
export function defineElementSetup(name, setup) {
  DEBUG: if (VERBOSE) console.info(MODULE, "define element", {name, setup});
  ASSERT: if (isDefined(elements[name]))
    console.warn(MODULE, "already defined element override", {
      name,
      setup,
      previous: elements[name],
    });

  elements[name] = setup;
}

/**
 * @example
 * ```js
 * defineAttributeSetup('href', (name) => {
 *   console.log(`stargazer found the first '<??? ${name}="???">'.`);
 * });
 * ```
 *
 * @template {string} Name
 *
 * @param {Name} name
 * The `name` specifies an {@link !Attr} by its
 * [name](https://developer.mozilla.org/docs/Web/API/Attr/name).
 *
 * @param {import('./types').Setup<Name>} setup
 * The `setup` is called **once** as soon as stargazer detects a specified {@link !Attr}
 * for the first time while compiling.
 */
export function defineAttributeSetup(name, setup) {
  DEBUG: if (VERBOSE) console.info(MODULE, "define attribute", {name, setup});
  ASSERT: if (isDefined(attributes[name])) {
    console.warn(MODULE, "already defined attribute override", {
      name,
      setup,
      previous: attributes[name],
    });
  }

  attributes[name] = setup;
}

/**
 * @type {import('./types').SetupMap}
 */
const elements = create();

/**
 * @type {import('./types').SetupMap}
 */
const attributes = create();

/**
 * @param {import('./types').SetupMap} setupMap
 * @returns {(name: string) => void}
 */
const UniqueSetup = (setupMap) => (name) => {
  const setup = setupMap[name];

  if (setup) {
    setup(name);
    setupMap[name] = null;
  }
};

/**
 * @internal
 */
export const setupElement = UniqueSetup(elements);

/**
 * @internal
 */
export const setupAttribute = UniqueSetup(attributes);
